import type { SearchEntry } from './SearchIndexFormat.js';
import { searchTokens } from './SearchText.js';

// Ranking in the browser over one language's index. Every query word of two or more
// letters must match somewhere in an entry (exactly, as a word prefix, with a typo, as another
// form of the same word or inside a word); one-letter words ("kat. b") only add to the score.

const FIELD_WEIGHTS = { title: 10, headings: 5, description: 4, terms: 1.5 } as const;
type Field = keyof typeof FIELD_WEIGHTS;

const EXACT = 1;
const PREFIX = 0.8;
const TYPO = 0.6;
const TYPO_IN_PREFIX = 0.45;
const STEM = 0.5;
const INSIDE = 0.5;
const TITLE_PHRASE_BONUS = 8;
const SCORE_CACHE_SIZE = 200;
// Fields whose adjacent word pairs are also indexed joined, so "bodypainting" meets "body painting".
const JOINED_PAIR_FIELDS: readonly Field[] = ['title', 'headings', 'description'];

interface Posting {
    entry: number;
    weight: number;
}

/** Edit distance with adjacent transpositions, or `max + 1` once it is certainly above `max`. */
export function typoDistance(a: string, b: string, max: number): number {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    let beforePrevious: number[] = [];
    let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
    for (let i = 1; i <= a.length; i++) {
        const current = [i];
        let rowMin = i;
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            let value = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + cost);
            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
                value = Math.min(value, beforePrevious[j - 2] + 1);
            }
            current.push(value);
            rowMin = Math.min(rowMin, value);
        }
        if (rowMin > max) return max + 1;
        beforePrevious = previous;
        previous = current;
    }
    return previous[b.length];
}

function allowedTypos(word: string): number {
    if (word.length >= 7) return 2;
    return word.length >= 4 ? 1 : 0;
}

/**
 * Another inflected form of the same word: Polish changes the ending and sometimes the last
 * letter of the stem ("brzuch" → "brzuszka", "lekarz" → "lekarskie"), more than a typo allows.
 */
function sameStem(query: string, word: string): boolean {
    let common = 0;
    while (common < query.length && query[common] === word[common]) common++;
    return common >= 4 && common >= query.length - 2;
}

/** How well a query word matches an indexed word (0 = not at all). */
export function wordMatch(query: string, word: string): number {
    if (word === query) return EXACT;
    if (query.length < 2) return 0;
    if (word.startsWith(query)) return PREFIX;
    const typos = allowedTypos(query);
    // Typos are looked for only in words starting like the query (or with its first two letters swapped):
    // it keeps the per-keystroke cost low and a typo in the first letter is rare.
    const sameStart = word[0] === query[0] || (word[0] === query[1] && word[1] === query[0]);
    if (typos && sameStart && typoDistance(query, word, typos) <= typos) return TYPO;
    // A word still being typed, with a typo: compare with the beginning of a longer word.
    if (
        typos &&
        sameStart &&
        word.length > query.length &&
        typoDistance(query, word.slice(0, query.length), typos) <= typos
    ) {
        return TYPO_IN_PREFIX;
    }
    if (sameStem(query, word)) return STEM;
    if (query.length >= 3 && word.includes(query)) return INSIDE;
    return 0;
}

export class SearchEngine {
    private readonly vocabulary = new Map<string, Posting[]>();
    private readonly foldedTitles: string[];
    private readonly scoresByWord = new Map<string, Map<number, number>>();

    constructor(private readonly entries: readonly SearchEntry[]) {
        this.foldedTitles = entries.map((entry) => searchTokens(entry.title).join(' '));
        entries.forEach((entry, index) => {
            const fields: Record<Field, string> = {
                title: entry.title,
                headings: entry.headings.join(' '),
                description: entry.description,
                terms: entry.terms,
            };
            for (const field of Object.keys(FIELD_WEIGHTS) as Field[]) {
                const tokens = searchTokens(fields[field]);
                const words = new Set(tokens);
                if (JOINED_PAIR_FIELDS.includes(field)) {
                    for (let i = 1; i < tokens.length; i++) words.add(tokens[i - 1] + tokens[i]);
                }
                for (const word of words) this.addPosting(word, index, FIELD_WEIGHTS[field]);
            }
        });
    }

    private addPosting(word: string, entry: number, weight: number): void {
        const postings = this.vocabulary.get(word) ?? [];
        const existing = postings.find((posting) => posting.entry === entry);
        if (existing) existing.weight = Math.max(existing.weight, weight);
        else postings.push({ entry, weight });
        this.vocabulary.set(word, postings);
    }

    search(query: string, limit: number): SearchEntry[] {
        const words = [...new Set(searchTokens(query))];
        if (!words.some((word) => word.length >= 2)) return [];

        // Per entry: summed score and how many required (two-letter or longer) words it matched.
        const matches = new Map<number, { score: number; required: number }>();
        const required = words.filter((word) => word.length >= 2).length;
        for (const word of words) {
            for (const [entry, score] of this.bestScores(word)) {
                const match = matches.get(entry) ?? { score: 0, required: 0 };
                match.score += score;
                if (word.length >= 2) match.required++;
                matches.set(entry, match);
            }
        }

        const phrase = searchTokens(query).join(' ');
        return [...matches]
            .filter(([, match]) => match.required === required)
            .map(([entry, match]) => {
                const titleBonus = this.foldedTitles[entry].includes(phrase) ? TITLE_PHRASE_BONUS : 0;
                return { entry, score: match.score + titleBonus };
            })
            .sort((a, b) => b.score - a.score || a.entry - b.entry)
            .slice(0, limit)
            .map(({ entry }) => this.entries[entry]);
    }

    /** The best score of each entry for one query word, over every indexed word it matches. */
    private bestScores(query: string): Map<number, number> {
        // While typing, only the last word changes: the earlier ones come from here.
        const cached = this.scoresByWord.get(query);
        if (cached) return cached;
        const best = new Map<number, number>();
        for (const [word, postings] of this.vocabulary) {
            const match = wordMatch(query, word);
            if (!match) continue;
            for (const { entry, weight } of postings) {
                best.set(entry, Math.max(best.get(entry) ?? 0, match * weight));
            }
        }
        if (this.scoresByWord.size >= SCORE_CACHE_SIZE) this.scoresByWord.clear();
        this.scoresByWord.set(query, best);
        return best;
    }
}
